/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { DateRange, isNullOrUndefined, TimestampRange, utc, UTC, guid, LQ } from 'rewire-common';
import { observable, freeze, createWatcherFn, WatcherTypeFn } from 'rewire-core';
import { ChangeTracker } from 'rewire-ui';
import {
  IMessage,
  IOtherPayment,
  ITimecard,
  ReasonCategories,
  IEmployeeTimecardStatus,
  IValidTransition,
  IEmployeeSummary,
  MessageFilterInput,
  ScheduleFilterInput,
  TimecardFilterInput,
  SaveTimecardInput,
  TimecardInput,
  IResult,
} from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import messagesSubscriptionService, {
  MessagesSubscriptionModel,
  MessageSort,
  defaultMessageSort,
} from '../services/MessagesSubscriptionService';
import { notifications } from '../ui/Notifications';
import ProxyArrayChangeTrackerContext from '../utility/ProxyArrayChangeTrackerContext';
import dailyRollupModel, { IRolledUpTimecard, DailyRollupModelBase } from './DailyRollupModel';
import RolledUpTimecard from './RolledUpTimecard';
import dailyTimecardModel, { DailyTimecardModelBase } from './DailyTimecardModel';
import MessagesModel from './MessagesModel';
import otherPaymentsModel, { OtherPaymentsModelBase } from './OtherPaymentsModel';
import summaryListModel from './SummaryListModel';
import mainPageModel from '../main-navigation/MainPageModel';

export const getTimecardsReturnSchema = `
  id
  earnedDate
  effective
  units
  factor
  rate
  amount
  accountCode
  shift
  timecardType
  reason {
    id
    name
    category
  }
  overtimeReason {
    id
    name
  }
  template {
    id
    name
    code
  }
  occupation {
    id
    name
  }
  employee {
    id
    name
  }
  job {
    id
    effective
    cycleOffset
    generator {
      name
      cycle {
        id
      }
    }
    group {
      id
      name
    }
    occupation {
      id
      name
    }
    employee {
      id
      name
    }
  }
  group {
    id
    name
    area {
      id
      name
    }
  }
  batch {
    id
    name
    status
  }
  tags
  fields
`;

interface ITimecardState {
  timecards: ITimecard[];
  reversals: ITimecard[];
  otherPayments: IOtherPayment[];
  statuses: IEmployeeTimecardStatus[];
  isLoading: boolean;
  batchName: string;
  canSubscribeToMessages: boolean;
  adjustments: boolean;
}

interface ISubscriptionState {
  employeeId?: string;
  effective?: DateRange;
}

function intersects(range1: DateRange, range2: DateRange) {
  return Math.max(range1.start.date, range2.start.date) <= Math.min(range1.end.date, range2.end.date);
}

export abstract class TimecardModelBase {
  state: ITimecardState;
  monitoringId: number;
  messagesModel: MessagesModel;
  timecardTracker: ChangeTracker;
  otherPaymentTracker: ChangeTracker;
  processRulesSubscription: any;
  subscriptionState: ISubscriptionState = { employeeId: undefined, effective: undefined };
  messagesSubscriptionModel: MessagesSubscriptionModel;
  watchMessages: WatcherTypeFn;
  handleProcessedRulesDebounced: (employeeId: string, effective: DateRange, changed?: boolean) => void;

  constructor(options?: { canSubscribeToMessages?: boolean }) {
    const timecards: ITimecard[] = [];
    const otherPayments: IOtherPayment[] = [];
    const statuses: IEmployeeTimecardStatus[] = [];

    this.state = observable({
      timecards,
      reversals: [],
      otherPayments,
      statuses,
      isLoading: false,
      adjustments: false,
      batchName: '',
      canSubscribeToMessages: !isNullOrUndefined(options?.canSubscribeToMessages) ? options!.canSubscribeToMessages : true,
    });

    const timecardContext: ProxyArrayChangeTrackerContext = new ProxyArrayChangeTrackerContext(
      this.timecards,
      this.isCompleteTimecard,
    );
    const otherPaymentContext: ProxyArrayChangeTrackerContext = new ProxyArrayChangeTrackerContext(
      this.otherPayments,
      this.isCompleteOtherPayment,
    );
    this.timecardTracker = new ChangeTracker(timecardContext);
    this.otherPaymentTracker = new ChangeTracker(otherPaymentContext);
    this.messagesModel = MessagesModel.create();

    const timecardValidationMessagesSchema = `
      id
      text
      level
      type
      effective
      employee {
        id
      }
    `;
    const timecardValidationMessagesFilter: MessageFilterInput = {
      types: ['validation:timecard', 'timecard:rule', 'timeoff', 'user'],
    };
    const timecardValidationMessagesSort: MessageSort = (m1: IMessage, m2: IMessage) => {
      if (m1.effective && m2.effective) {
        if (m1.effective < m2.effective) {
          return -1;
        }
        if (m1.effective > m2.effective) {
          return 1;
        }
      }
      return defaultMessageSort(m1, m2);
    };

    this.messagesSubscriptionModel = messagesSubscriptionService.createSubscriptionModel({
      returnSchema: timecardValidationMessagesSchema,
      filter: timecardValidationMessagesFilter,
      sort: timecardValidationMessagesSort,
    });
    this.watchMessages = createWatcherFn();
    requestAnimationFrame(() => this.initialize());
  }

  abstract get rollupModel(): DailyRollupModelBase;
  abstract get dailyTimecardModel(): DailyTimecardModelBase;
  abstract get otherPaymentsModel(): OtherPaymentsModelBase;
  abstract get effective(): DateRange | undefined;
  abstract get employees(): IEmployeeSummary[];
  abstract get selectedEmployee(): IEmployeeSummary | undefined;
  abstract get viewOnly(): boolean;
  abstract createRollup(earnedDate: UTC, timecards: ITimecard[], status: IEmployeeTimecardStatus | null): IRolledUpTimecard;

  reset() {
    timecardModel.closeSubscription();
    this.messagesSubscriptionModel.reset();
    this.watchMessages();
  }

  commit() {
    this.timecardTracker.commit();
    this.otherPaymentTracker.commit();
    this.dailyTimecardModel.commit();
  }

  startMonitoringTimecardChanges() {
    this.monitoringId = setInterval(() => this.calculateHasChanges(), 1_000);
  }

  stopMonitoringTimecardChanges() {
    clearInterval(this.monitoringId);
  }

  closeSubscription() {
    this.processRulesSubscription?.unsubscribe();
    this.processRulesSubscription = undefined;
    this.subscriptionState = { employeeId: undefined, effective: undefined };
  }

  openSubscription(employeeId?: string, effective?: DateRange) {
    if (
      employeeId &&
      effective &&
      this.subscriptionState.employeeId === employeeId &&
      this.subscriptionState.effective &&
      this.subscriptionState.effective.equals(effective)
    )
      return;
    this.closeSubscription();

    if (!employeeId || !effective) return;
    this.subscriptionState = { employeeId, effective: effective.clone() };
    this.processRulesSubscription = DataQueries.subscribeToProcessedRules(employeeId, effective, (data: any) => {
      this.handleProcessedRules(employeeId, effective, data.changed);
    });
  }

  _notification?: { employeeId: string; effective: DateRange; changed: boolean };
  handleProcessedRules(employeeId: string, effective: DateRange, changed: boolean) {
    const employee = this.selectedEmployee;
    if (employee?.id !== employeeId || !employee?.saving) {
      if (this._notification) {
        this._notification.changed = this._notification.changed || changed;
        return;
      }

      this._notification = { employeeId, effective, changed };
      setTimeout(async () => {
        try {
          const currentEffective = this.effective;

          if (this._notification?.changed === false) {
            notifications.info('No changes detected after running rules');
            // reloading in case there were changes to worked units (including start/end times) that will cause the reason-specific columns to be recalculated
            if (currentEffective) this.rollupModel.load(this.rollupModel?.selectedRolledUpTimecard?.earnedDate);
            return;
          }

          if (this.hasChanges) {
            notifications.warning('Not reloading after running rules - you have unsaved changes');
            return;
          }

          const currentEmployeeId = this.selectedEmployee?.id;

          if (!currentEmployeeId || employeeId !== currentEmployeeId) {
            notifications.warning('Not reloading after running rules - employee has changed');
            return;
          }

          if (!currentEffective || !intersects(effective, currentEffective)) {
            notifications.warning('Not reloading after running rules - date range has changed');
            return;
          }

          notifications.info('Loading updated timecard after running rules');
          await this.load();
        } finally {
          this._notification = undefined;
        }
      }, 2000);
    }
  }

  get isLoading(): boolean {
    return this.state.isLoading;
  }

  set isLoading(value: boolean) {
    this.state.isLoading = value;
  }

  get adjustments(): boolean {
    return this.state.adjustments;
  }

  set adjustments(value: boolean) {
    this.state.adjustments = value;
  }

  get selectedEmployeeId(): string | undefined {
    return this.selectedEmployee?.id;
  }

  get timecards(): ITimecard[] {
    return this.state.timecards;
  }

  get reversals(): ITimecard[] {
    return this.state.reversals;
  }

  get otherPayments(): IOtherPayment[] {
    return this.state.otherPayments;
  }

  get statuses(): IEmployeeTimecardStatus[] {
    return this.state.statuses;
  }

  get hasChanges(): boolean {
    const hasChanges =
      this.timecardTracker.hasChanges || this.otherPaymentTracker.hasChanges || this.dailyTimecardModel.hasChanges();
    return hasChanges;
  }

  get batchName(): string {
    return this.state.batchName;
  }

  set batchName(batchName: string) {
    this.state.batchName = batchName;
  }

  get canEditTimecard(): boolean {
    return !this.viewOnly;
  }

  get canSubscribeToMessages(): boolean {
    return this.state.canSubscribeToMessages;
  }

  get allMessages(): IMessage[] {
    const allMessages = [...this.messagesModel.messages, ...this.messagesSubscriptionModel.messages];
    allMessages.sort(this.messagesSubscriptionModel.sort);
    return allMessages;
  }

  get allSelectedEmployeeMessages(): IMessage[] {
    const allMessages = [
      ...this.messagesModel.messages,
      ...this.messagesSubscriptionModel.messages.filter(
        (m) => this.selectedEmployeeId && m.employee?.id === this.selectedEmployeeId,
      ),
    ];
    allMessages.sort(this.messagesSubscriptionModel.sort);
    return allMessages;
  }

  addMessage = async (message: IMessage) => {
    this.messagesModel.add(message);
  };

  removeMessage = async (message: IMessage) => {
    this.messagesModel.remove(message);
  };

  removeMessagesForId = async (id: string) => {
    this.messagesModel.removeForId(id);
  };

  protected async initialize() {
    await this.rollupModel.load();
    this.otherPaymentsModel.load();
    this.messagesModel.set([]);
  }

  updateErrorWarningCountsForEmployees() {
    requestAnimationFrame(() => {
      for (const e of this.employees) {
        e.error = 0;
        e.warning = 0;
        e.info = 0;
        for (const message of LQ(this.messagesModel.messages).concat(this.messagesSubscriptionModel.messages).iterator) {
          const messageLevel = message.level;
          if (e.id !== message.employee?.id || !messageLevel || typeof (e as any)[messageLevel] !== 'number') continue;
          (e as any)[messageLevel]++;
        }
      }
    });
  }

  protected processResults(_timecards: ITimecard[], _statuses: IEmployeeTimecardStatus[]) {
    const timecards: ITimecard[] = [];
    const reversals: ITimecard[] = [];
    const otherPayments: IOtherPayment[] = [];
    const statuses: IEmployeeTimecardStatus[] = [];

    for (const status of _statuses) {
      status.earnedDate = utc(status.earnedDate);
      statuses.push(status);
    }

    this.batchName = '';

    for (const timecard of _timecards) {
      if (this.isReversal(timecard)) {
        if (!this.batchName) this.batchName = timecard.batch?.name || '';

        timecard.start = timecard.effective?.start;
        timecard.end = timecard.effective?.end;
        reversals.push(timecard);
      } else if (this.isTimecard(timecard)) {
        if (!this.batchName) this.batchName = timecard.batch?.name || '';

        timecard.start = timecard.effective?.start;
        timecard.end = timecard.effective?.end;
        // timecard.tags           = timecard.tags;
        // timecard.fields         = timecard.fields;
        // timecard.overtimeReason = timecard.overtimeReason;
        // timecard.template       = timecard.template;
        // timecard.rate           = timecard.rate;
        // timecard.factor         = timecard.factor;
        // timecard.accountCode    = timecard.accountCode;

        timecards.push(timecard);
      } else {
        const otherPayment: IOtherPayment = this.createOtherPayment(timecard);
        otherPayments.push(otherPayment);
      }
    }

    const dataEntryPayment = this.createDataEntryOtherPaymentIfNecessary(timecards, otherPayments);
    if (dataEntryPayment) {
      otherPayments.push(observable(dataEntryPayment));
    }

    this.reversals.set(reversals);
    this.timecards.set(timecards);
    this.timecardTracker.set(this.timecards);

    this.otherPayments.set(otherPayments);
    this.otherPaymentTracker.set(this.otherPayments);

    this.statuses.set(statuses);
  }

  protected isTimecard(timecard: ITimecard) {
    // it's a timecard if:
    //    1. there's no reason specified
    //    2. the reason has no category (not likely)
    //    3. the reason's category is NOT 'other'
    return timecard.timecardType !== 'r' && (!timecard.reason?.category || timecard.reason.category !== ReasonCategories.Other);
  }

  protected isReversal(timecard: ITimecard) {
    // it's a reversal if the adjustment type is 'r'
    return timecard.timecardType === 'r';
  }

  handleMessageSelected(message: IMessage) {
    this.rollupModel.handleMessageSelected(message);
  }

  recalculateOtherPaymentAmount(otherPayment: IOtherPayment) {
    if (isNullOrUndefined(otherPayment?.units) || isNullOrUndefined(otherPayment?.rate) || isNullOrUndefined(otherPayment?.factor))
      return;

    const amount = otherPayment.units! * otherPayment.rate! * otherPayment.factor!;
    otherPayment.amount = amount;
  }

  // deleteTimecard = async (timecard: ITimecard) => {
  //   const index = this.timecards.indexOf(timecard);
  //   this.timecards.splice(index, 1);
  //   this.removeMessagesForId(timecard.id);
  //   const rolledUpTimecard = this.rollupModel.rolledUpTimecards.find((rolledUpTimecard: IRolledUpTimecard) => rolledUpTimecard.earnedDate.equals(timecard.earnedDate));
  //   if (rolledUpTimecard) {
  //     await this.rollupModel.updateRollup(rolledUpTimecard.id);
  //   }
  //   this.dailyTimecardModel.load();
  // }

  // confirmDeleteTimecard(jobInfo: JobInfoInput[]) {
  //   confirmationModel.load(
  //     'Are you sure you want to delete this timecard?',
  //     {
  //       yesAction: () => {
  //         this.executeDeleteTimecard(jobInfo);
  //         return true;
  //       },
  //       title: 'Confirm Timecard Deletion',
  //       maxWidth: 'sm'
  //     }
  //   );
  // }

  // executeDeleteTimecard = async (jobInfo: JobInfoInput[]) => {
  //   const result = await DataMutations.deleteJobTimecards(jobInfo);
  //   if (result) {
  //     const selectedRolledUpTimecard            = this.rollupModel.selectedRolledUpTimecard!;
  //     selectedRolledUpTimecard.timecards.length = 0;
  //     notifications.success('Timecard Deleted');
  //     await this.load();
  //   }
  // }

  deleteOtherPayment = (otherPayment: IOtherPayment) => {
    const index = this.otherPayments.indexOf(otherPayment);
    this.otherPayments.splice(index, 1);
    this.removeMessagesForId(otherPayment.id);
  };

  protected clearState() {
    this.isLoading = false;
    this.timecards.length = 0;
    this.otherPayments.length = 0;
    this.timecardTracker.commit();
    this.otherPaymentTracker.commit();
    this.dailyTimecardModel.commit();
  }

  get status(): string | undefined {
    const theStatus = Array.from(this.rollupModel.getTimecards()).reduce((prev: string | undefined, current: IRolledUpTimecard) => {
      if (prev === undefined) return current.status;
      if (prev !== current.status) return '«mixed»';
      return prev;
    }, undefined);
    return theStatus;
  }

  get isPayable(): boolean {
    return this.statuses.reduce((payable: boolean, status: IEmployeeTimecardStatus) => payable && status.isPayable, true);
  }

  get allowableTransitions(): IValidTransition[] {
    const transitions: IValidTransition[] = [];

    for (const rolledUpTimecard of Array.from(this.rollupModel.getTimecards())) {
      for (const transition of rolledUpTimecard.allowableTransitions) {
        const existingTransition = transitions.find((t) => t.event === transition.event);
        if (existingTransition) {
          existingTransition.enabled = existingTransition.enabled || transition.enabled;
        } else {
          // push a copy of the transition onto the list - otherwise, it's possible the enabled value can be modified above (existingTransition.enabled = ...)
          transitions.push(Object.assign({}, transition));
        }
      }
    }
    return transitions;
  }

  executeTransition = async (event: string) => {
    if (this.viewOnly || !this.effective || !this.selectedEmployeeId) return;
    let newStatuses: IEmployeeTimecardStatus[] | undefined;
    if (this.adjustments) {
      const earnedDates = LQ(this.timecards)
        .distinct((t) => t.earnedDate.utc)
        .map((t) => utc(t))
        .toArray();

      const results = await Promise.all(
        earnedDates.map((t) => DataMutations.transitionTimecards([this.selectedEmployeeId!], new DateRange(t, t), event)),
      );
      for (const result of results) {
        if (result?.length) {
          if (!newStatuses) newStatuses = [];
          newStatuses.push(...result);
        }
      }
    } else {
      newStatuses = await DataMutations.transitionTimecards([this.selectedEmployeeId], this.effective, event);
    }

    if (!newStatuses || !newStatuses.length) return;

    for (const status of newStatuses) {
      const rolledUpTimecard: IRolledUpTimecard | undefined = LQ(this.rollupModel.getTimecards())
        .filter((t) => t.earnedDate.equals(status.earnedDate))
        .first();
      if (rolledUpTimecard) {
        rolledUpTimecard.setStatus(status);
      }
    }
  };

  async load(selectedBatchId?: string, filters?: ScheduleFilterInput) {
    this.isLoading = true;
    this.adjustments = filters?.adjustments ?? false;
    try {
      this.messagesModel.reset();
      this.watchMessages(
        () => {
          this.messagesModel.messages.length;
          this.messagesSubscriptionModel.messages.length;
        },
        () => {
          this.updateErrorWarningCountsForEmployees();
        },
      );
      this.openSubscription(this.selectedEmployeeId, this.effective);
      this.messagesSubscriptionModel.filter = {
        types: ['validation:timecard', 'timecard:rule', 'timeoff', 'user'],
        effective: this.effective,
        areaIds: filters?.areaIds,
        groupIds: filters?.groupIds,
        batchIds: filters?.batchIds,
        employeeIds: this.employees.map((e) => e.id),
      };
      if (this.canSubscribeToMessages) {
        await this.messagesSubscriptionModel.startUpdate();
      }
      if (!this.effective || !this.selectedEmployeeId) {
        freeze(() => this.clearState());
      } else {
        const [timecards, statuses] = await this.getData(this.selectedEmployeeId, this.effective, selectedBatchId, filters);
        this.processResults(timecards.data as ITimecard[], statuses as IEmployeeTimecardStatus[]);
      }
      await this.refreshModels();
    } finally {
      this.isLoading = false;
    }
  }

  protected async getData(
    employeeId: string,
    effective: DateRange,
    batchId?: string,
    filters?: ScheduleFilterInput,
  ): Promise<[IResult<ITimecard>, IEmployeeTimecardStatus[]]> {
    const sort: any = { earnedDate: { direction: 'asc' } };
    const filter: TimecardFilterInput = {
      employeeIds: [employeeId],
      adjustments: filters?.adjustments,
    };

    if (batchId) {
      filter.batchIds = [batchId];
      this.adjustments = true;
    }
    return await Promise.all([
      DataQueries.getTimecards(getTimecardsReturnSchema, effective, filter, sort, 200) as Promise<IResult<ITimecard>>,
      DataQueries.getTimecardStatuses([employeeId], effective!, filter),
    ]);
  }

  protected refreshModels = async () => {
    const selectedEarnedDate = this.rollupModel.selectedRolledUpTimecard?.earnedDate;
    this.rollupModel.selectedRolledUpTimecard = undefined;
    await this.rollupModel.load(selectedEarnedDate);
    this.otherPaymentsModel.load();
  };

  async runRulesMutation(employeeId: string, effective: DateRange): Promise<boolean> {
    const result = await DataMutations.runTimecardRules(employeeId, effective);
    return !!result;
  }

  async saveMutation(timecards: SaveTimecardInput[]): Promise<boolean> {
    const result = await DataMutations.saveEmployeeTimecards(timecards);
    return !!result;
  }

  async runRules(): Promise<boolean> {
    if (!this.canRunRules()) return false;
    const effective = this.effective;
    const employeeId = this.selectedEmployee?.id;
    if (!effective || !employeeId) return false;

    try {
      const result = await this.runRulesMutation(employeeId, effective);
      return result;
    } catch (_) {}

    return false;
  }

  async save(): Promise<boolean> {
    if (!this.canSave()) return false;
    const employee = this.selectedEmployee;
    if (!this.effective || !employee) return false;

    let result: boolean = false;
    employee.saving = true;

    try {
      const timecards = this.generateTimecardsForSave(employee.id);

      result = await this.saveMutation(timecards);
      if (result) {
        this.commit();
        notifications.success(`Timecard changes saved for ${employee.name}`);
      }
      return result;
    } catch (ex) {
      notifications.error(ex);
      return false;
    } finally {
      employee.saving = false;
    }
  }

  protected generateTimecardsForSave = (employeeId: string): SaveTimecardInput[] => {
    if (!this.effective) return [];
    const timecards: SaveTimecardInput[] = [];
    const changedRows = this.getChangedRows();
    for (const earnedDate of this.effective) {
      if (!changedRows.filter((r) => r.earnedDate?.utc === earnedDate.utc).length) continue;
      const t: SaveTimecardInput = { earnedDate: utc(earnedDate), employeeId, timecards: [] };
      timecards.push(t);

      for (const timecard of this.timecards) {
        if (timecard.earnedDate.utc !== earnedDate.utc || !this.isCompleteTimecard(timecard)) continue;
        t.timecards?.push({
          id: timecard.id,
          employeeId: employeeId,
          earnedDate: timecard.earnedDate,
          templateId: timecard.template?.id,
          reasonId: timecard.reason?.id,
          overtimeReasonId: timecard.overtimeReason?.id,
          groupId: timecard.group?.id,
          tags: timecard.tags,
          occupationId: timecard.occupation?.id,
          jobId: timecard.job?.id,
          effective:
            timecard.start && timecard.end && timecard.start.isValid && timecard.end.isValid
              ? new TimestampRange(timecard.start, timecard.end)
              : null,
          units: timecard.units,
          factor: timecard.factor,
          rate: timecard.rate,
          amount: timecard.amount,
          accountCode: timecard.accountCode,
          fields: timecard.fields,
          createdBy: timecard.createdBy,
          createdDate: timecard.createdDate ?? UTC.now(),
          shift: timecard.shift,
        } as TimecardInput);
      }

      for (const timecard of this.otherPayments) {
        if (timecard.earnedDate?.utc !== earnedDate.utc || !this.isCompleteOtherPayment(timecard)) continue;
        if (!timecard.group?.id)
          throw new Error(
            'you are trying to create an other payment but there is no group information available. Please assign this employee to a group first and try again.',
          );
        t.timecards?.push({
          id: timecard.id,
          employeeId: employeeId,
          earnedDate: timecard.earnedDate,
          templateId: timecard.template?.id,
          reasonId: timecard.reason?.id,
          groupId: timecard.group?.id,
          occupationId: timecard.occupation?.id,
          units: timecard.units,
          factor: timecard.factor,
          rate: timecard.rate,
          amount: timecard.amount,
          accountCode: timecard.accountCode,
          createdBy: timecard.createdBy,
          createdDate: timecard.createdDate ?? UTC.now(),
          shift: 0,
        } as TimecardInput);
      }
    }

    return timecards;
  };

  async cancel() {
    if (this.viewOnly) return;
    freeze(() => {
      this.timecardTracker.revert();
      this.messagesModel.set([]);
    });
    this.revertOtherPayments();
    await this.refreshModels();
  }

  revertOtherPayments() {
    this.otherPaymentTracker.revert();
    const dataEntryPayment = this.createDataEntryOtherPaymentIfNecessary(this.timecards, this.otherPayments);
    if (dataEntryPayment) {
      this.otherPayments.push(observable(dataEntryPayment));
    }
  }

  isTimecardDateValid(timecard: ITimecard | IOtherPayment, earnedDate: UTC): boolean {
    if (!timecard.earnedDate?.isValid) return false;
    if (!timecard.earnedDate.equals(earnedDate)) return false;
    return true;
  }

  isNotInBatch(earnedDate: UTC): boolean {
    const rolledUpTimecard = LQ(this.rollupModel.getTimecards())
      .filter((rolledUpTimecard: IRolledUpTimecard) => rolledUpTimecard.earnedDate.equals(earnedDate))
      .first();
    // return true;
    return rolledUpTimecard?.batchStatus === 'not_in_batch';
  }

  isCompleteTimecard(t: ITimecard): boolean {
    return (
      !isNullOrUndefined(t.group) && !isNullOrUndefined(t.occupation) && !isNullOrUndefined(t.units) && !isNullOrUndefined(t.reason)
    );
  }

  isCompleteOtherPayment(op: IOtherPayment): boolean {
    return (
      !isNullOrUndefined(op.earnedDate) &&
      !isNullOrUndefined(op.units) &&
      !isNullOrUndefined(op.rate) &&
      !isNullOrUndefined(op.factor) &&
      !isNullOrUndefined(op.reason)
    );
  }

  getChangedRows() {
    const timecardChanges = this.timecardTracker.getChangedRows();
    const otherPaymentChanges = this.otherPaymentTracker.getChangedRows();
    return [...timecardChanges, ...otherPaymentChanges];
  }

  requiresDataEntryOtherPayment(otherPayments: IOtherPayment[]): boolean {
    if (!this.otherPaymentsModel.canEditTimecard) return false;
    if (!otherPayments.length) return true;

    const t = otherPayments[otherPayments.length - 1];
    return this.isCompleteOtherPayment(t);
  }

  createMinimalOtherPayment(): IOtherPayment {
    return { id: guid(), hideDeleteIcon: true };
  }

  protected createOtherPayment(timecard: ITimecard): IOtherPayment {
    return {
      id: timecard.id,
      earnedDate: timecard.earnedDate,
      rate: timecard.rate,
      factor: timecard.factor,
      units: timecard.units,
      amount: timecard.units! * timecard.rate! * timecard.factor!,
      accountCode: timecard.accountCode,
      reason: timecard.reason,
      // batch:       batch,
      occupation: timecard.occupation,
      group: timecard.group,
    } as IOtherPayment;
  }

  determineSortOrder(timecard: ITimecard): number {
    const category = timecard.reason?.category;
    if (!category) return 6;

    switch (category) {
      case 'worked':
        return timecard.overtimeReason ? 2 : 1;

      case 'premium':
        return timecard.overtimeReason ? 4 : 3;

      case 'absence':
        return 5;

      default:
        return 7;
    }
  }

  sortTimecards(timecards: ITimecard[]) {
    timecards.sort((t1, t2) => {
      if (t1.earnedDate.utc !== t2.earnedDate.utc) return t1.earnedDate.utc - t2.earnedDate.utc;
      if (!this.isCompleteTimecard(t1) && this.isCompleteTimecard(t2)) return 1;
      if (this.isCompleteTimecard(t1) && !this.isCompleteTimecard(t2)) return -1;

      const rank1 = t1.start ? t1.start.utc : Number.POSITIVE_INFINITY;
      const rank2 = t2.start ? t2.start.utc : Number.POSITIVE_INFINITY;
      const sortOrder = this.determineSortOrder(t1) - this.determineSortOrder(t2);

      if (rank1 !== rank2) return rank1 - rank2;
      if (sortOrder !== 0) return sortOrder;

      // start values not present
      // if end values are present, use them as a secondary sort
      return t1.end && t2.end
        ? t1.end.utc - t2.end.utc
        : // otherwise, consider the entries equal (for sorting purposes)
          0;
    });
  }

  createDataEntryOtherPaymentIfNecessary(timecards: ITimecard[], otherPayments: IOtherPayment[]): IOtherPayment | null {
    if (!this.requiresDataEntryOtherPayment(otherPayments)) return null;

    for (const op of otherPayments) {
      op.hideDeleteIcon = false;
    }

    // create data entry other payment with bare minimum of data
    const dataEntryOtherPayment: IOtherPayment = this.createMinimalOtherPayment();

    const allTimecards = this.timecards.filter(this.isCompleteTimecard);
    this.evaluateForField(timecards, allTimecards, dataEntryOtherPayment, 'group');
    this.evaluateForField(timecards, allTimecards, dataEntryOtherPayment, 'occupation');
    return dataEntryOtherPayment;
  }

  evaluateForField(
    timecards: ITimecard[],
    allTimecards: ITimecard[],
    newTimecard: ITimecard | IOtherPayment,
    field: keyof ITimecard,
  ) {
    // find the last record in the selection for which there is a value for 'field'
    // if a record is found, assign its value for 'field' to 'newTimecard'
    for (let i = timecards.length - 1; i >= 0; --i) {
      const timecard: ITimecard = timecards[i];
      if (timecard[field]) {
        (newTimecard as any)[field] = timecard[field];
        return;
      }
    }
    // if no record is found, evaluate all timecards that have been loaded (likely for the week)
    for (let i = allTimecards.length - 1; i >= 0; --i) {
      const timecard = allTimecards[i];
      if (timecard[field]) {
        (newTimecard as any)[field] = timecard[field];
        return;
      }
    }
  }

  protected calculateHasChanges() {
    if (this.isLoading) {
      return;
    }

    this.timecardTracker.recalculate();
    this.otherPaymentTracker.recalculate();
  }

  canChangeStatus() {
    return !this.viewOnly && this.isPayable;
  }

  canRunRules() {
    return !this.viewOnly && !!this.selectedEmployee && !this.canSave();
  }

  canSave() {
    return !this.viewOnly && this.hasChanges;
  }
}

export class TimecardModel extends TimecardModelBase {
  constructor() {
    super();

    this.dailyTimecardModel.editTimecard.addListener('save', this.onSave);
    requestAnimationFrame(() => {
      summaryListModel.canSave = this.canSave.bind(this);
      summaryListModel.save = this.save.bind(this);
    });
  }

  get rollupModel(): DailyRollupModelBase {
    return dailyRollupModel;
  }

  get dailyTimecardModel(): DailyTimecardModelBase {
    return dailyTimecardModel;
  }

  get otherPaymentsModel(): OtherPaymentsModelBase {
    return otherPaymentsModel;
  }

  get effective(): DateRange | undefined {
    return summaryListModel.effective;
  }

  get employees(): IEmployeeSummary[] {
    return summaryListModel.employees;
  }

  get selectedEmployee(): IEmployeeSummary | undefined {
    return summaryListModel.selectedEmployee;
  }

  get viewOnly(): boolean {
    return mainPageModel.timecardViewOnly;
  }

  protected onSave = () => {
    setTimeout(() => this.save(), 100);
  };

  createRollup(earnedDate: UTC, timecards: ITimecard[], status: IEmployeeTimecardStatus | null): IRolledUpTimecard {
    return RolledUpTimecard.create(earnedDate, timecards, status);
  }
}

const timecardModel = new TimecardModel();
export default timecardModel;
